import { useMemo } from 'react';
import { InputPhoneNumber } from '@/components/Input/Phone/InputPhoneNumber';
import type { PhoneValue } from '@/components/Input/Phone/state.machine';
import { VStack } from '@/components/Layout';
import { ControlledSelect } from '@/components/Select/ControlledSelect';
import { Text } from '@/components/Typography/Text';
import type { PhoneType } from '@/features/section-attribute/types';
import { useFormattedPhoneNumber } from '@/hooks/useFormattedPhoneNumber';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
export const JAPAN_DIAL_CODE = '81';
const phoneTypeRecord: Record<PhoneType, true> = {
  MOBILE: true,
  LANDLINE: true
};
export const PHONE_TYPES = Object.keys(phoneTypeRecord) as PhoneType[];
export type PhoneItemValue = PhoneValue & {
  phoneType?: PhoneType | null;
};
type Props = CFormItemProps & {
  value: PhoneItemValue;
  onChange: (value: PhoneItemValue) => void;
  showPhoneType?: boolean;
};
export const CFormPhoneItem = buildCFormItem((props: Props) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.phoneItem'
  });
  const value: PhoneItemValue = props.value ?? {
    dialCode: JAPAN_DIAL_CODE,
    phoneNumber: ''
  };
  const {
    formattedFull
  } = useFormattedPhoneNumber(value);
  const phoneTypeOptions = useMemo(() => PHONE_TYPES.map(v => ({
    value: v,
    label: String(t(`phoneType.${v}`))
  })), [t]);
  const phoneTypeLabel = useMemo(() => {
    if (!value.phoneType) return undefined;
    return phoneTypeOptions.find(o => o.value === value.phoneType)?.label;
  }, [value.phoneType, phoneTypeOptions]);
  if (!props.showPhoneType) {
    return <CFormItemBase {...props} readComponent={formattedFull} writeComponent={<InputPhoneNumber value={value} onChange={props.onChange} required={props.required} />} />;
  }
  return <CFormItemBase {...props} readComponent={<VStack className="gap-1">
          {phoneTypeLabel && <Text variant="pSmall" className="text-neutral-muted-on-fill">
              {phoneTypeLabel}
            </Text>}
          <Text variant="labelLarge">{formattedFull}</Text>
        </VStack>} writeComponent={<VStack className="gap-3">
          <VStack className="gap-1">
            <Text variant="label" className="text-neutral-muted-on-fill">
              {t('phoneType.label')}
            </Text>
            <ControlledSelect id={`${props.id}-phoneType`} value={value.phoneType ?? ''} label={phoneTypeLabel} onChange={option => props.onChange({
        ...value,
        phoneType: option?.value as PhoneType || undefined
      })} fetchOptions={async () => phoneTypeOptions} allowEmpty disabled={props.readonly} hideSearch width="max-w-56" />
          </VStack>
          <VStack className="gap-1">
            <Text variant="label" className="text-neutral-muted-on-fill">
              {props.label}
            </Text>
            <InputPhoneNumber value={value} onChange={phone => props.onChange({
        ...value,
        ...phone
      })} required={props.required} />
          </VStack>
        </VStack>} />;
});